import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "api/axios";
import { User } from "auth/AuthProvider";
import { Role } from "types";
import Loader from "components/Loader";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

interface UserDetailsContextType {
  userInfo: User | null;
  isLoading: boolean;
  error: string | null;
  editUser: (data: Partial<User>) => Promise<void>;
  deactivateUser: () => Promise<void>;
  fetchUserRoles: () => Promise<void>;
  updateWorkspaceAdmin: (isAdmin: boolean) => Promise<void>;
  roles: Role[];
}

const UserDetailsContext = createContext<UserDetailsContextType | undefined>(
  undefined
);

export const useUserInfo = () => {
  const context = useContext(UserDetailsContext);
  if (!context) {
    throw new Error("useUserInfo must be used within a UserDetailsProvider");
  }
  return context;
};

interface UserDetailsProviderProps {
  children: ReactNode;
}

export const UserInfoProvider = ({ children }: UserDetailsProviderProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserRoles = useCallback(async () => {
    try {
      const roles = (
        await api.get(`/users/${id}/groups`, {
          params: {
            skip: 0,
            take: 999,
          },
        })
      ).data.items;

      setRoles(roles);
    } catch (error) {
      handleAxiosAndCaughtError(error);
    }
  }, [id]);

  const fetchUserDetails = useCallback(async () => {
    if (userInfo) return;
    try {
      setIsLoading(true);
      setError(null);
      const [foundUser] = await Promise.all([
        (await api.get(`/users/${id}`)).data,
        fetchUserRoles(),
      ]);
      if (!foundUser) {
        throw new Error(t("users.userInfo.errors.userNotFound"));
      }
      setUserInfo(foundUser);
      // setUserInfo(response.data);
    } catch (err) {
      setError(t("users.userInfo.errors.failedToFetchUserDetails"));
      console.error("Error fetching user details:", err);
      // Redirect to "/" on error
      navigate("/dashboard/users", { replace: true });
      return;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, userInfo, id, fetchUserRoles]);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
    }
  }, [id, fetchUserDetails]);

  const editUser = async (data: Partial<User>) => {
    try {
      setIsLoading(true);
      setError(null);
      await api.put<User>(`/users/${id}`, data);
      const user = (await api.get(`/users/${id}`)).data;
      setUserInfo(user);
    } catch (err) {
      setError(t("users.userInfo.errors.failedToUpdateUserDetails"));

      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const deactivateUser = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await api.put(`/users/${id}/deactivate`);
    } catch (err) {
      setError(t("users.userInfo.errors.failedToDeactivateUser"));

      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateWorkspaceAdmin = async (isAdmin: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      await api.put(`/workspaces/users/${id}/admin`, {
        is_admin: isAdmin,
      });
      // Refresh user info to get updated workspace admin status
      const user = (await api.get(`/users/${id}`)).data;
      setUserInfo(user);
    } catch (err) {
      setError(t("users.userInfo.errors.failedToUpdateUserDetails"));
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    userInfo,
    isLoading,
    error,
    editUser,
    deactivateUser,
    fetchUserRoles,
    updateWorkspaceAdmin,
    roles,
  };

  return (
    <UserDetailsContext.Provider value={value}>
      {isLoading ? (
        <Loader
          className="absolute left-1/2 top-1/2 -translate-y-1/2 translate-x-1/2"
          txt={t("users.userInfo.loadingUserInformation")}
        />
      ) : (
        children
      )}
    </UserDetailsContext.Provider>
  );
};
